/**
 * Back closes one layer (DCTA-84 A1). Android's Back gesture/button — and the browser's — is
 * history: without entries it closed the app and the session with it. Every layer the app
 * opens (the chat, an account's detail, a sheet) pushes one entry; `popstate` closes every
 * layer newer than the entry the browser landed on. A layer that must not close now (a
 * ceremony in flight, a locked sheet) refuses: its entry is pushed back, nothing else moves.
 * A layer that closes itself (its own back button, a drag, Done) calls `done()`, which
 * consumes its entry so the next Back is not spent on it.
 */
interface HistoryLike {
  readonly state: unknown;
  pushState(state: unknown, unused: string): void;
  back(): void;
}

interface WindowLike {
  readonly history: HistoryLike;
  addEventListener(type: "popstate", fn: (e: { state: unknown }) => void): void;
}

interface Layer {
  readonly id: number;
  /** Close it; `false` = not now (it stays open, with its entry). */
  readonly pop: () => boolean;
}

const idOf = (state: unknown): number => {
  const v = (state as { dcta?: unknown } | null)?.dcta;
  return typeof v === "number" ? v : 0;
};

export class Nav {
  readonly #w: WindowLike;
  #stack: Layer[] = [];
  #seq = 0;

  constructor(w: WindowLike) {
    this.#w = w;
    // Entries from an earlier page life count from where they are.
    this.#seq = idOf(w.history.state);
    w.addEventListener("popstate", (e) => this.#landed(idOf(e.state)));
  }

  /** Layers open now. */
  get open(): number {
    return this.#stack.length;
  }

  push(pop: () => boolean): number {
    const id = ++this.#seq;
    this.#w.history.pushState({ dcta: id }, "");
    this.#stack.push({ id, pop });
    return id;
  }

  /**
   * QA-04: a reload brought back the screen that was open — its entry is the one the browser is on, from the page's
   * earlier life. It takes that entry back instead of pushing another (so one Back still closes it), when the entry is
   * ours and no layer is open yet; anything else is a push.
   */
  adopt(pop: () => boolean): number {
    const id = idOf(this.#w.history.state);
    if (id === 0 || this.#stack.length > 0) return this.push(pop);
    this.#stack.push({ id, pop });
    return id;
  }

  /**
   * One layer closes as the next opens (DCTA-85 P2-3: an account's detail or the chat giving way
   * to Activity): the new layer takes over the closing one's entry. `done()` then `push()` would
   * race — `history.back()` is asynchronous, and its popstate would land under the new entry and
   * close it. A layer that is not open gets a fresh entry.
   */
  swap(id: number, pop: () => boolean): number {
    const i = this.#stack.findIndex((l) => l.id === id);
    if (i === -1) return this.push(pop);
    this.#stack[i] = { id, pop };
    return id;
  }

  /** The layer closed itself: its entry goes, and nothing runs again. */
  done(id: number): void {
    const i = this.#stack.findIndex((l) => l.id === id);
    if (i === -1) return;
    const top = i === this.#stack.length - 1;
    this.#stack.splice(i, 1);
    // The newest entry is consumed at once; an older one stays behind in the browser's list
    // and costs one extra Back later — it closes nothing (`#landed` only closes newer layers).
    if (top) this.#w.history.back();
  }

  /** Signed out: no layer is open any more. */
  reset(): void {
    this.#stack = [];
  }

  #landed(current: number): void {
    for (let top = this.#stack.at(-1); top !== undefined && top.id > current; top = this.#stack.at(-1)) {
      this.#stack.pop();
      if (!top.pop()) {
        this.#w.history.pushState({ dcta: top.id }, "");
        this.#stack.push(top);
        return;
      }
    }
  }
}
